import type { PaginatedResponse } from './commonResponseTypes';
import type { TaskData } from './customKanbanTypes';
import type { StoryPointsSummaryType } from './tasks';

export enum SprintStatus {
  PLANNED = 'PLANNED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED',
}

export interface SprintSummaryDetails {
  id: string;
  name: string;
  status: SprintStatus;
  startDate: string;
  endDate: string;
  goal?: string;
  capacityInStoryPoints?: number;
  completedAt?: string;
  hasCloseSnapshot?: boolean;
  carryToSprintId?: string | null;
  autoManageEnabled?: boolean;
  autoManageSpaceId?: string;
  autoManageStartTime?: string;
  autoManageEndTime?: string;
  autoManageTimeZone?: string;
  autoManageLastOwnerRequestAt?: string | null;
  autoManageLastOwnerRequestSignature?: string | null;
  autoManageLastRunAt?: string | null;
  autoManageLastSummaryAt?: string | null;
}

export interface SprintListItem extends SprintSummaryDetails {
  totalTasks: number;
  remainingStoryPoints?: number;
  completedStoryPoints?: number;
}

export interface SprintSummaryDetailsApiResponse
  extends Omit<SprintSummaryDetails, 'id'> {
  _id: string;
}

export interface SprintListItemApiResponse extends Omit<SprintListItem, 'id'> {
  _id: string;
}

export interface SprintStatusCounts {
  TODO: number;
  IN_PROGRESS: number;
  COMPLETED: number;
  CANCELED: number;
}

export interface SprintBoardSummary {
  totalTasks: number;
  statusCounts: SprintStatusCounts;
  totalStoryPoints: number;
  completedStoryPoints: number;
  remainingStoryPoints: number;
  capacityInStoryPoints?: number;
  progressPercentage: number;
  overCapacity: boolean;
}

export interface PersonStoryPointOverviewItem {
  personId: string | null;
  displayName: string;
  email?: string | null;
  pendingTaskCount: number;
  pendingStoryPoints: number;
}

export interface SprintBoardResponse {
  sprint: SprintSummaryDetails;
  summary: SprintBoardSummary;
  tasks: TaskData[];
  storyPointsSummary: StoryPointsSummaryType;
  personStoryPointOverview: PersonStoryPointOverviewItem[];
}

export interface SprintBoardApiResponse
  extends Partial<PaginatedResponse<TaskData[]>> {
  taskStatus?: string;
  sprint: SprintSummaryDetails;
  summary: SprintBoardSummary;
  storyPointsSummary: StoryPointsSummaryType;
  personStoryPointOverview?: PersonStoryPointOverviewItem[];
  tasks?: TaskData[];
}

export interface SprintListApiResponse
  extends PaginatedResponse<SprintListItemApiResponse[]> {}

export interface SprintTotalsResponse {
  planned: number;
  active: number;
  completed: number;
  archived: number;
}

export interface CreateSprintPayload {
  name: string;
  startDate: string;
  endDate: string;
  goal?: string;
  capacityInStoryPoints?: number;
  status?: SprintStatus;
}

export type UpdateSprintPayload = Partial<CreateSprintPayload>;

export interface SprintWorkingHours {
  startTime: string;
  endTime: string;
  timeZone: string;
}

export interface UpdateSprintAutoManagePayload {
  autoManageEnabled: boolean;
  autoManageSpaceId?: string | null;
  autoManageWorkingHours?: SprintWorkingHours | null;
}

export interface SprintTaskBulkUpdatePayload {
  taskIds: string[];
}

export interface SprintBacklogRequestPayload {
  search?: string;
  filter: Record<string, unknown>;
  sort?: Record<string, 'ascending' | 'descending'>;
}

export interface SprintActiveSummaryResponse {
  sprint: SprintSummaryDetails | null;
  summary: SprintBoardSummary | null;
  personStoryPointOverview: PersonStoryPointOverviewItem[];
}
